/**
 * Language Wrapper Utility for Personality Interpretation
 *
 * Provides context-specific content for different view modes
 * (applicant vs hiring-manager) using the existing i18n system.
 */

/**
 * View mode type definition
 * @typedef {'applicant'|'hiring_manager'} ViewMode
 */

/**
 * Language wrapper for different view contexts
 * Uses translation keys from the database instead of hardcoded strings
 */
export const LANGUAGE_WRAPPER = {
	/**
	 * Get language wrapper data for specific view mode
	 * @param {ViewMode} viewMode - The view context ('applicant' or 'hiring_manager')
	 * @returns {Object} Language wrapper object with all context-specific strings
	 */
	getWrapper: viewMode => {
		const isValidViewMode =
			viewMode === 'applicant' || viewMode === 'hiring_manager'
		const mode = isValidViewMode ? viewMode : 'applicant'

		return {
			tabLabel: `personality_results.view.${mode}.tab_label`,
			cardTitle: `personality_results.view.${mode}.card_title`,
			cardDescription: `personality_results.view.${mode}.card_description`,
			profileTypePrefix: `personality_results.view.${mode}.profile_type_prefix`,
			sectionTitles: {
				keyStrengths: `personality_results.view.${mode}.section.key_strengths`,
				workStyle: `personality_results.view.${mode}.section.work_style`,
				communicationStyle: `personality_results.view.${mode}.section.communication_style`,
				decisionMaking: `personality_results.view.${mode}.section.decision_making`,
				conflictResolution: `personality_results.view.${mode}.section.conflict_resolution`,
				workEnvironment: `personality_results.view.${mode}.section.work_environment`,
				collaborationStyle: `personality_results.view.${mode}.section.collaboration_style`,
				adaptabilityToChange: `personality_results.view.${mode}.section.adaptability_to_change`
			}
		}
	},

	/**
	 * Get section title for a specific view mode and section
	 * @param {ViewMode} viewMode - The view context
	 * @param {string} section - The section key
	 * @returns {string} Section title
	 */
	getSectionTitle: (viewMode, section) => {
		const mode =
			viewMode === 'hiring_manager' ? 'hiring_manager' : 'applicant'
		return `personality_results.view.${mode}.section.${section}`
	},

	/**
	 * Get view-specific label
	 * @param {ViewMode} viewMode - The view context
	 * @param {string} labelType - Type of label (tab_label, card_title, etc.)
	 * @returns {string} Localized label
	 */
	getLabel: (viewMode, labelType) => {
		const mode =
			viewMode === 'hiring_manager' ? 'hiring_manager' : 'applicant'
		return `personality_results.view.${mode}.${labelType}`
	}
}

export function getAxisMotivation(axisDirectionKey) {
	if (!axisDirectionKey) return null
	return `personality_results.axis_direction.${axisDirectionKey}`
}

/**
 * Display name mappings for traits and quadrants
 * Uses translation keys from the database
 */
export const DISPLAY_NAME = {
	/**
	 * Get display name for a trait
	 * @param {string} trait - Trait name (e.g., 'supporter', 'reformer')
	 * @returns {string} Localized trait name
	 */
	getTraitName: trait => {
		return `personality_results.trait.${trait}.name`
	},

	/**
	 * Get display name for a quadrant
	 * @param {string} quadrant - Quadrant color ('red', 'yellow', 'green', 'blue')
	 * @returns {string} Localized quadrant label
	 */
	getQuadrantName: quadrant => {
		return `personality_results.quadrant.${quadrant}.label`
	},

	/**
	 * Get workplace signal for a trait
	 * @param {string} trait - Trait name
	 * @returns {string} Localized workplace signal
	 */
	getTraitWorkplaceSignal: trait => {
		return `personality_results.trait.${trait}.workplace_signal`
	},

	/**
	 * Get workplace signal for an axis
	 * @param {string} axis - Axis color ('green', 'blue', 'yellow', 'red')
	 * @returns {string} Localized axis workplace signal
	 */
	getAxisWorkplaceSignal: axis => {
		return `personality_results.axis_insights.${axis}.workplace_signal`
	}
}

/**
 * Interpretation copy constants
 * Uses translation keys from the database
 */
export const INTERPRETATION_COPY = {
	/**
	 * Get profile-related copy
	 * @param {string} key - Copy key
	 * @returns {string} Localized copy
	 */
	getProfileCopy: key => {
		return `personality_results.profile.${key}`
	},

	/**
	 * Get general interpretation copy
	 * @param {string} key - Copy key
	 * @returns {string} Localized copy
	 */
	getGeneralCopy: key => {
		return `personality_results.${key}`
	}
}

/**
 * Trait data accessor using translation keys
 * Replaces hardcoded TRAIT_DATA with database-driven content
 */
export const TRAIT_DATA = {
	/**
	 * Get trait-specific data
	 * @param {string} trait - Trait name
	 * @returns {Object} Trait data with all localized content
	 */
	getTraitData: trait => {
		return {
			name: `personality_results.trait.${trait}.name`,
			workplaceSignal: `personality_results.trait.${trait}.workplace_signal`,
			description: `personality_results.trait.${trait}.description`,
			keyStrengths: [
				`personality_results.trait.${trait}.key_strengths.1`,
				`personality_results.trait.${trait}.key_strengths.2`,
				`personality_results.trait.${trait}.key_strengths.3`
			],
			workStyle: [
				`personality_results.trait.${trait}.work_style.1`,
				`personality_results.trait.${trait}.work_style.2`,
				`personality_results.trait.${trait}.work_style.3`
			],
			communicationStyle: `personality_results.trait.${trait}.communication_style`,
			decisionMaking: `personality_results.trait.${trait}.decision_making`,
			conflictResolution: `personality_results.trait.${trait}.conflict_resolution`,
			workEnvironment: `personality_results.trait.${trait}.work_environment`,
			collaborationStyle: `personality_results.trait.${trait}.collaboration_style`,
			adaptabilityToChange: `personality_results.trait.${trait}.adaptability_to_change`
		}
	}
}

/**
 * Axis insights data accessor using translation keys
 * Replaces hardcoded AXIS_INSIGHTS_DATA with database-driven content
 */
export const AXIS_INSIGHTS_DATA = {
	/**
	 * Get axis-specific data
	 * @param {string} axis - Axis color ('green', 'blue', 'yellow', 'red')
	 * @returns {Object} Axis data with all localized content
	 */
	getAxisData: axis => {
		return {
			workplaceSignal: `personality_results.axis_insights.${axis}.workplace_signal`,
			description: `personality_results.axis_insights.${axis}.description`,
			keyStrengths: [
				`personality_results.axis_insights.${axis}.key_strengths.1`,
				`personality_results.axis_insights.${axis}.key_strengths.2`,
				`personality_results.axis_insights.${axis}.key_strengths.3`
			],
			workStyle: [
				`personality_results.axis_insights.${axis}.work_style.1`,
				`personality_results.axis_insights.${axis}.work_style.2`,
				`personality_results.axis_insights.${axis}.work_style.3`
			],
			communicationStyle: `personality_results.axis_insights.${axis}.communication_style`,
			decisionMaking: `personality_results.axis_insights.${axis}.decision_making`,
			conflictResolution: `personality_results.axis_insights.${axis}.conflict_resolution`,
			workEnvironment: `personality_results.axis_insights.${axis}.work_environment`,
			collaborationStyle: `personality_results.axis_insights.${axis}.collaboration_style`,
			adaptabilityToChange: `personality_results.axis_insights.${axis}.adaptability_to_change`
		}
	}
}

/**
 * Directional insights data accessor using translation keys.
 * Covers all 8 directional profiles:
 *   - 4 existing (routine/change primary): delegates to axis_insights.{color}.*
 *   - 4 new (people/facts primary): uses directional_insights.{key}.*
 */
export const DIRECTIONAL_INSIGHTS_DATA = {
	/**
	 * Map of existing directional keys → their quadrant color
	 * (no content duplication — these profiles reuse axis_insights strings)
	 */
	_existingKeyToColor: {
		routine_people: 'green',
		routine_facts: 'blue',
		change_people: 'yellow',
		change_facts: 'red'
	},

	/** Keys that have dedicated directional_insights strings */
	_newKeys: ['people_change', 'people_routine', 'facts_change', 'facts_routine'],

	/**
	 * Get insight translation keys for a directional profile.
	 *
	 * @param {string} directionalKey - e.g. 'routine_people', 'people_change'
	 * @returns {Object|null} Object of translation key strings, or null if unknown key
	 */
	getDirectionalData(directionalKey) {
		if (!directionalKey) return null

		// Existing profiles — map to the corresponding axis_insights color
		if (this._existingKeyToColor[directionalKey]) {
			const color = this._existingKeyToColor[directionalKey]
			return {
				workplaceSignal: `personality_results.axis_insights.${color}.workplace_signal`,
				signalDescription: `personality_results.axis_insights.${color}.signal_description`,
				description: `personality_results.axis_insights.${color}.description`,
				keyStrengths: [
					`personality_results.axis_insights.${color}.key_strengths.1`,
					`personality_results.axis_insights.${color}.key_strengths.2`,
					`personality_results.axis_insights.${color}.key_strengths.3`
				],
				workStyle: [
					`personality_results.axis_insights.${color}.work_style.1`,
					`personality_results.axis_insights.${color}.work_style.2`,
					`personality_results.axis_insights.${color}.work_style.3`
				],
				communicationStyle: `personality_results.axis_insights.${color}.communication_style`,
				decisionMaking: `personality_results.axis_insights.${color}.decision_making`,
				conflictResolution: `personality_results.axis_insights.${color}.conflict_resolution`,
				workEnvironment: `personality_results.axis_insights.${color}.work_environment`,
				collaborationStyle: `personality_results.axis_insights.${color}.collaboration_style`,
				adaptabilityToChange: `personality_results.axis_insights.${color}.adaptability_to_change`
			}
		}

		// New profiles — use dedicated directional_insights namespace
		if (this._newKeys.includes(directionalKey)) {
			const ns = `personality_results.directional_insights.${directionalKey}`
			return {
				workplaceSignal: `${ns}.workplace_signal`,
				signalDescription: `${ns}.signal_description`,
				description: `${ns}.description`,
				keyStrengths: [
					`${ns}.key_strengths.1`,
					`${ns}.key_strengths.2`,
					`${ns}.key_strengths.3`
				],
				workStyle: [
					`${ns}.work_style.1`,
					`${ns}.work_style.2`,
					`${ns}.work_style.3`
				],
				communicationStyle: `${ns}.communication_style`,
				decisionMaking: `${ns}.decision_making`,
				conflictResolution: `${ns}.conflict_resolution`,
				workEnvironment: `${ns}.work_environment`,
				collaborationStyle: `${ns}.collaboration_style`,
				adaptabilityToChange: `${ns}.adaptability_to_change`
			}
		}

		return null
	}
}

/**
 * Quadrant display names using translation keys
 */
export const QUADRANT_DISPLAY_NAMES = {
	/**
	 * Get quadrant display name
	 * @param {string} quadrant - Quadrant color
	 * @returns {string} Localized quadrant display name
	 */
	getQuadrantDisplayName: quadrant => {
		return `personality_results.quadrant.${quadrant}.label`
	}
}

// Default export for convenience
export default {
	LANGUAGE_WRAPPER,
	DISPLAY_NAME,
	getAxisMotivation,
	INTERPRETATION_COPY,
	TRAIT_DATA,
	AXIS_INSIGHTS_DATA,
	DIRECTIONAL_INSIGHTS_DATA,
	QUADRANT_DISPLAY_NAMES
}
